import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Rating,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AppointmentResponseDto, AppointmentStatus } from '../../../../types/appointment.types';
import { UserAvatar } from '../../../common/UserAvatar';
import { BaseDialog } from '../../../common/BaseDialog';
import { ModalIndices } from '../../../../common/i-doc/i-doc.registry';
import { AppointmentCountdown } from '../../AppointmentCountdown';

const BASE_STATUS_LABELS: Record<AppointmentStatus, string> = {
  [AppointmentStatus.PENDING]:   'Потребує підтвердження',
  [AppointmentStatus.CONFIRMED]: 'Підтверджено',
  [AppointmentStatus.CANCELLED]: 'Скасовано',
  [AppointmentStatus.COMPLETED]: 'Завершено',
};

function getExpertStatusLabel(a: AppointmentResponseDto): string {
  if (a.status === AppointmentStatus.CANCELLED) {
    if (a.cancelledBy === 'expert') return 'Скасовано вами';
    if (a.cancelledBy === 'client') return 'Скасовано клієнтом';
  }
  return BASE_STATUS_LABELS[a.status];
}

const STATUS_COLORS: Record<AppointmentStatus, 'warning' | 'success' | 'error' | 'default'> = {
  [AppointmentStatus.PENDING]:   'warning',
  [AppointmentStatus.CONFIRMED]: 'success',
  [AppointmentStatus.CANCELLED]: 'error',
  [AppointmentStatus.COMPLETED]: 'default',
};

interface Props {
  selected: AppointmentResponseDto | null;
  modalLoading: boolean;
  onClose: () => void;
  onConfirm: (id: number) => void;
  onOpenCancel: (appointment: AppointmentResponseDto) => void;
  onOpenComplete: (appointment: AppointmentResponseDto) => void;
  onOpenReschedule: (appointment: AppointmentResponseDto) => void;
  onOpenOutcome: () => void;
}

export const ExpertAppointmentDetailsDialog: React.FC<Props> = ({
  selected,
  modalLoading,
  onClose,
  onConfirm,
  onOpenCancel,
  onOpenComplete,
  onOpenReschedule,
  onOpenOutcome,
}) => {
  const navigate = useNavigate();

  return (
    <BaseDialog
      open={selected !== null || modalLoading}
      onClose={onClose}
      title="Деталі зустрічі"
      iDoc={ModalIndices.EXPERT_APPOINTMENT_DETAILS}
      actions={
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => selected && navigate(`/client/${selected.client.id}`)}
            disabled={!selected}
          >
            Профіль
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {selected?.status === AppointmentStatus.PENDING && (
              <Button size="small" variant="contained" color="primary" onClick={() => onConfirm(selected.id)}>
                Підтвердити
              </Button>
            )}
            {selected?.status === AppointmentStatus.CONFIRMED && (
              <Button size="small" variant="contained" color="success" onClick={() => onOpenComplete(selected)}>
                Завершити
              </Button>
            )}
            {/* E1: reschedule лише для CONFIRMED. У PENDING — confirm або cancel з причиною. */}
            {selected?.status === AppointmentStatus.CONFIRMED && (
              <Button size="small" variant="outlined" onClick={() => onOpenReschedule(selected)}>
                Перенести
              </Button>
            )}
            {selected?.status === AppointmentStatus.COMPLETED && (
              <Button size="small" variant="outlined" onClick={onOpenOutcome}>
                {selected.outcome ? 'Редагувати підсумок' : 'Додати підсумок'}
              </Button>
            )}
            {(selected?.status === AppointmentStatus.PENDING || selected?.status === AppointmentStatus.CONFIRMED) && (
              <Button size="small" color="error" onClick={() => onOpenCancel(selected)}>
                Скасувати
              </Button>
            )}
          </Box>
        </Box>
      }
    >
      {modalLoading || !selected ? (
        <Box display="flex" justifyContent="center" sx={{ py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <UserAvatar path={selected.client.avatar} name={selected.client.name} sx={{ width: 44, height: 44 }} />
              <Typography variant="h6">{selected.client.name}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Дата</Typography>
            <Typography variant="body2">{new Date(selected.scheduledAt).toLocaleString('uk-UA')}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Тривалість</Typography>
            <Typography variant="body2">{selected.durationMinutes} хв.</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Статус</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Chip
                label={getExpertStatusLabel(selected)}
                color={STATUS_COLORS[selected.status]}
                size="small"
                variant="outlined"
              />
              <AppointmentCountdown scheduledAt={selected.scheduledAt} status={selected.status} />
            </Box>
          </Grid>
          {selected.clientNotes && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Нотатки клієнта</Typography>
              <Typography variant="body2">{selected.clientNotes}</Typography>
            </Grid>
          )}
          {selected.expertNotes && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Нотатки експерта</Typography>
              <Typography variant="body2">{selected.expertNotes}</Typography>
            </Grid>
          )}
          {selected.outcome && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Підсумок зустрічі</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selected.outcome}</Typography>
            </Grid>
          )}
          {selected.cancellationReason && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Причина скасування</Typography>
              <Typography variant="body2">{selected.cancellationReason}</Typography>
            </Grid>
          )}
          {selected.review && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Відгук клієнта</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Rating value={selected.review.rating} readOnly size="small" />
                <Typography variant="body2" color="text.secondary">{selected.review.rating}/5</Typography>
              </Box>
              {selected.review.comment && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>{selected.review.comment}</Typography>
              )}
            </Grid>
          )}
        </Grid>
      )}
    </BaseDialog>
  );
};
